import { useState, useMemo } from 'react';
import { Search, Users, Loader2, AlertCircle, Plus, Filter } from 'lucide-react';
import type { UserRole } from 'scheduler-types';
import { usePeople } from '../hooks/usePeople';
import { PersonCard } from '../components/PersonCard';
import { PersonEditModal } from '../components/PersonEditModal';
import { SkillsManager } from '../components/SkillsManager';

const roleOptions: { value: UserRole | ''; label: string }[] = [
  { value: '', label: 'All Roles' },
  { value: 'planner', label: 'Planners' },
  { value: 'worker', label: 'Workers' },
  { value: 'contractor', label: 'Contractors' },
  { value: 'account_manager', label: 'Account Managers' },
  { value: 'leadership', label: 'Leadership' },
  { value: 'procurement', label: 'Procurement' },
  { value: 'viewer', label: 'Viewer' },
];

export function PeoplePage() {
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editingPersonId, setEditingPersonId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: people, isLoading, error } = usePeople({
    role: roleFilter || undefined,
    isActive: showInactive ? undefined : true,
  });

  // Client-side search filtering
  const filteredPeople = useMemo(() => {
    if (!people) return [];
    if (!searchQuery.trim()) return people;

    const query = searchQuery.toLowerCase();
    return people.filter(
      (person) =>
        person.name.toLowerCase().includes(query) ||
        person.email.toLowerCase().includes(query)
    );
  }, [people, searchQuery]);

  const handleAddPerson = () => {
    setEditingPersonId(null);
    setIsModalOpen(true);
  };

  const handleEditPerson = (personId: string) => {
    setEditingPersonId(personId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPersonId(null);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">People</h1>
          <p className="text-sm text-foreground-muted mt-1">
            Manage team members, skills, and availability
          </p>
        </div>
        <button
          onClick={handleAddPerson}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Person
        </button>
      </div>

      {/* Skills Management (collapsible) */}
      <SkillsManager />

      {/* Filters */}
      <div className="bg-background rounded-lg border border-border p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Role filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-foreground-muted" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as UserRole | '')}
              className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Show inactive toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm text-foreground-muted">Show inactive</span>
          </label>
        </div>

        {/* Results count */}
        {people && (
          <p className="text-sm text-foreground-muted mt-3">
            Showing {filteredPeople.length} of {people.length} people
          </p>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-danger bg-danger-subtle p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-danger" />
            <div>
              <h3 className="font-medium text-danger-foreground">
                Failed to load people
              </h3>
              <p className="text-sm text-danger-foreground mt-1">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      ) : filteredPeople.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-background p-8 text-center">
          <Users className="h-8 w-8 text-foreground-muted mx-auto mb-3" />
          <h3 className="text-lg font-medium text-foreground">No people found</h3>
          <p className="text-sm text-foreground-muted mt-1">
            {searchQuery
              ? 'Try adjusting your search query'
              : 'Add team members to get started'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPeople.map((person) => (
            <PersonCard
              key={person.id}
              person={person}
              onClick={() => handleEditPerson(person.id)}
            />
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <PersonEditModal
        personId={editingPersonId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}
